import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { TIDS } from "@/constants/testIds";
import { Menu, X } from "lucide-react";

const linkBase =
  "text-[13px] font-medium tracking-wide text-zinc-300 hover:text-white transition-colors uppercase";
const active = "text-[var(--primary)]";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid={TIDS.navLogo}
          className="flex items-center gap-2 group"
        >
          <span className="font-heading font-black text-lg tracking-tight text-white whitespace-nowrap">
            E-Quantum&nbsp;<span className="text-[var(--primary)]">Consults</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <NavLink
            to="/services"
            data-testid={TIDS.navServices}
            className={({ isActive }) => `${linkBase} ${isActive ? active : ""}`}
          >
            Services
          </NavLink>
          <NavLink
            to="/about"
            data-testid={TIDS.navAbout}
            className={({ isActive }) => `${linkBase} ${isActive ? active : ""}`}
          >
            About
          </NavLink>
          <NavLink
            to="/pricing"
            data-testid={TIDS.navPricing}
            className={({ isActive }) => `${linkBase} ${isActive ? active : ""}`}
          >
            Pricing
          </NavLink>
          <NavLink
            to="/audit"
            data-testid="nav-audit"
            className={({ isActive }) => `${linkBase} ${isActive ? active : ""}`}
          >
            Free Audit
          </NavLink>
          <NavLink
            to="/booking"
            data-testid={TIDS.navBooking}
            className={({ isActive }) => `${linkBase} ${isActive ? active : ""}`}
          >
            Book
          </NavLink>
          <NavLink
            to="/contact"
            data-testid={TIDS.navContact}
            className={({ isActive }) => `${linkBase} ${isActive ? active : ""}`}
          >
            Contact
          </NavLink>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user && user.role === "admin" && (
            <Link to="/admin" data-testid={TIDS.navAdmin} className="btn-ghost">
              Admin
            </Link>
          )}
          {user ? (
            <>
              <Link to="/royalties" data-testid="nav-royalties" className={linkBase}>
                Royalties
              </Link>
              <Link to="/dashboard" data-testid={TIDS.navDashboard} className="btn-ghost">
                Portal
              </Link>
              <button
                onClick={handleLogout}
                data-testid={TIDS.navLogout}
                className="btn-primary"
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid={TIDS.navLogin} className="btn-ghost">
                Sign in
              </Link>
              <Link to="/register" data-testid={TIDS.navRegister} className="btn-primary">
                Join
              </Link>
            </>
          )}
        </div>

        <button
          className="lg:hidden text-white"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-black/95">
          <div className="px-6 py-6 flex flex-col gap-4">
            {[
              ["/services", "Services", TIDS.navServices],
              ["/about", "About", TIDS.navAbout],
              ["/pricing", "Pricing", TIDS.navPricing],
              ["/audit", "Free Audit", "nav-audit-mobile"],
              ["/booking", "Book", TIDS.navBooking],
              ["/contact", "Contact", TIDS.navContact],
            ].map(([to, label, tid]) => (
              <Link
                key={to}
                to={to}
                data-testid={`m-${tid}`}
                className={linkBase + " text-base"}
                onClick={() => setOpen(false)}
              >
                {label}
              </Link>
            ))}
            <div className="h-px bg-white/10 my-2" />
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" className="btn-ghost" onClick={() => setOpen(false)}>
                    Admin
                  </Link>
                )}
                <Link to="/royalties" className="btn-ghost" onClick={() => setOpen(false)}>
                  Royalties
                </Link>
                <Link to="/dashboard" className="btn-ghost" onClick={() => setOpen(false)}>
                  Portal
                </Link>
                <button onClick={handleLogout} className="btn-primary">
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-ghost" onClick={() => setOpen(false)}>
                  Sign in
                </Link>
                <Link to="/register" className="btn-primary" onClick={() => setOpen(false)}>
                  Join
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
